import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  FaBook, 
  FaGraduationCap, 
  FaChartLine, 
  FaCalendarAlt,
  FaUsers,
  FaVideo,
  FaFileAlt,
  FaQuestionCircle,
  FaTrophy,
  FaClock,
  FaCheckCircle,
  FaPlayCircle,
  FaDownload,
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaChartBar,
  FaUserGraduate,
  FaUserTie,
  FaCrown,
  FaCog,
  FaRobot,
  FaGamepad,
  FaComments,
  FaMobileAlt,
  FaSearch,
  FaArrowRight,
  FaTicketAlt
} from 'react-icons/fa';
import { getCourses } from '../../features/courses/courseSlice';
import { getUserEnrollments } from '../../features/enrollments/enrollmentSlice';
import { getUserProgress } from '../../features/progress/progressSlice';
import { toggleDarkMode } from '../../features/ui/uiSlice';

// Import feature components
import AIAssistant from '../../components/dashboard/AIAssistant';
import AdvancedAnalytics from '../../components/dashboard/AdvancedAnalytics';
import Gamification from '../../components/dashboard/Gamification';
import CommunicationHub from '../../components/dashboard/CommunicationHub';
import MobileFeatures from '../../components/dashboard/MobileFeatures';
import SmartContentDiscovery from '../../components/dashboard/SmartContentDiscovery';
import StudentList from '../../components/dashboard/StudentList';
import StudentDetails from '../../components/dashboard/StudentDetails';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { enrollments, isLoading: enrollmentsLoading } = useSelector((state) => state.enrollments);
  const { courses, isLoading: coursesLoading } = useSelector((state) => state.courses);
  const { progress, isLoading: progressLoading } = useSelector((state) => state.progress);
  
  const [stats, setStats] = useState({
    totalCourses: 0,
    completedCourses: 0,
    inProgressCourses: 0,
    averageProgress: 0,
    totalStudents: 0,
    totalRevenue: 0,
    pendingApprovals: 0
  });

  const { darkMode } = useSelector((state) => state.ui);

  // Mock data for demonstration
  const upcomingDeadlines = [
    { id: 1, title: 'JavaScript Assignment', course: 'JavaScript Basics', dueDate: '2023-12-15', type: 'assignment' },
    { id: 2, title: 'React Quiz', course: 'Advanced React', dueDate: '2023-12-20', type: 'quiz' },
    { id: 3, title: 'Final Project', course: 'Node.js Fundamentals', dueDate: '2023-12-25', type: 'assignment' },
  ];

  const recentActivities = [
    { id: 1, type: 'completed', item: 'Introduction to HTML', course: 'Web Development Basics', date: '2023-12-01' },
    { id: 2, type: 'started', item: 'React Hooks', course: 'Advanced React', date: '2023-12-02' },
    { id: 3, type: 'enrolled', item: 'Node.js Fundamentals', date: '2023-12-03' },
  ];
  
  // Mock data for student management
  const mockEnrollments = [
    { _id: 'e1', userId: 'student001', courseId: 'course001', enrollmentDate: '2023-11-15' },
    { _id: 'e2', userId: 'student002', courseId: 'course002', enrollmentDate: '2023-11-20' },
    { _id: 'e3', userId: 'student003', courseId: 'course003', enrollmentDate: '2023-11-25' },
    { _id: 'e4', userId: 'student004', courseId: 'course004', enrollmentDate: '2023-11-30' },
  ];
  
  const mockProgress = [
    { _id: 'p1', userId: 'student001', courseId: 'course001', completionPercentage: 75, lastActivity: '2023-12-01' },
    { _id: 'p2', userId: 'student002', courseId: 'course002', completionPercentage: 45, lastActivity: '2023-12-02' },
    { _id: 'p3', userId: 'student003', courseId: 'course003', completionPercentage: 90, lastActivity: '2023-12-03' },
    { _id: 'p4', userId: 'student004', courseId: 'course004', completionPercentage: 30, lastActivity: '2023-12-04' },
  ];
  
  const mockCourses = [
    { _id: 'course001', title: 'JavaScript Fundamentals', instructor: 'instructor001', isPublished: true, category: 'Programming', level: 'Beginner' },
    { _id: 'course002', title: 'Advanced React Development', instructor: 'instructor002', isPublished: true, category: 'Web Development', level: 'Advanced' },
    { _id: 'course003', title: 'Node.js Backend', instructor: 'instructor001', isPublished: true, category: 'Backend', level: 'Intermediate' },
    { _id: 'course004', title: 'Mobile App Development', instructor: 'instructor003', isPublished: false, category: 'Mobile', level: 'Intermediate' },
  ];

  useEffect(() => {
    dispatch(getCourses());
    dispatch(getUserEnrollments());
    dispatch(getUserProgress());
  }, [dispatch]);

  useEffect(() => {
    if (enrollments.length && progress) {
      calculateStats();
    }
  }, [enrollments, progress, user]);

  const calculateStats = () => {
    if (user?.role === 'student') {
      // Student stats
      const totalCourses = enrollments.length;
      const completedCourses = enrollments.filter(
        (enrollment) => {
          const courseProgress = progress.find(p => p.courseId === enrollment.courseId);
          return courseProgress && courseProgress.completionPercentage === 100;
        }
      ).length;
      
      const inProgressCourses = totalCourses - completedCourses;
      
      const totalProgress = progress.reduce(
        (sum, p) => sum + (p.completionPercentage || 0), 
        0
      );
      const averageProgress = totalCourses > 0 
        ? Math.round(totalProgress / totalCourses) 
        : 0;

      setStats({
        totalCourses,
        completedCourses,
        inProgressCourses,
        averageProgress,
        totalStudents: 0,
        totalRevenue: 0,
        pendingApprovals: 0
      });
    } else if (user?.role === 'instructor') {
      // Instructor stats
      const instructorCourses = courses.filter(course => course.instructor === user._id);
      const totalStudents = enrollments.filter(enrollment => 
        instructorCourses.some(course => course._id === enrollment.courseId)
      ).length;
      
      setStats({
        totalCourses: instructorCourses.length,
        completedCourses: 0,
        inProgressCourses: 0,
        averageProgress: 0,
        totalStudents,
        totalRevenue: 0,
        pendingApprovals: instructorCourses.filter(course => !course.isPublished).length
      });
    } else if (user?.role === 'admin') {
      // Admin stats
      setStats({
        totalCourses: courses.length,
        completedCourses: 0,
        inProgressCourses: 0,
        averageProgress: 0,
        totalStudents: enrollments.length,
        totalRevenue: 0,
        pendingApprovals: courses.filter(course => !course.isPublished).length
      });
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getDaysRemaining = (dueDate) => {
    const today = new Date();
    const deadline = new Date(dueDate);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getRoleBasedDashboard = () => {
    switch (user?.role) {
      case 'student':
        return <StudentDashboard />;
      case 'instructor':
        return <InstructorDashboard />;
      case 'admin':
        return <AdminDashboard />;
      default:
        return <StudentDashboard />;
    }
  };

  const StudentDashboard = () => {
    const [activeFeatureTab, setActiveFeatureTab] = useState('aiAssistant');
    
    // Function to render the active feature component
    const renderFeatureComponent = () => {
      switch(activeFeatureTab) {
        case 'aiAssistant':
          return <AIAssistant />;
        case 'analytics':
          return <AdvancedAnalytics />;
        case 'gamification':
          return <Gamification />;
        case 'communication':
          return <CommunicationHub />;
        case 'mobile':
          return <MobileFeatures />;
        case 'discover':
          return <SmartContentDiscovery />;
        default:
          return <AIAssistant />;
      }
    };
    
    return (
      <>
        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
                <FaBook className="text-xl" />
              </div>
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Courses</p>
                <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>{stats.totalCourses}</h3>
              </div>
            </div>
          </div>
          
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                <FaGraduationCap className="text-xl" />
              </div>
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Completed</p>
                <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>{stats.completedCourses}</h3>
              </div>
            </div>
          </div>
          
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 text-yellow-600 mr-4">
                <FaChartLine className="text-xl" />
              </div>
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>In Progress</p>
                <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>{stats.inProgressCourses}</h3>
              </div>
            </div>
          </div>
          
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
                <FaTrophy className="text-xl" />
              </div>
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Average Progress</p>
                <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>{stats.averageProgress}%</h3>
              </div>
            </div>
          </div>
        </div>
        
        {/* New Feature Navigation */}
        <div className="mb-8">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow overflow-hidden`}>
            <div className={`flex flex-wrap ${darkMode ? 'border-gray-700' : 'border-b'}`}>
              <button 
                className={`px-6 py-3 text-sm font-medium flex items-center ${activeFeatureTab === 'aiAssistant' 
                  ? 'border-b-2 border-primary text-primary' 
                  : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveFeatureTab('aiAssistant')}
              >
                <FaRobot className="mr-2" />
                AI Assistant
              </button>
              <button 
                className={`px-6 py-3 text-sm font-medium flex items-center ${activeFeatureTab === 'analytics' 
                  ? 'border-b-2 border-primary text-primary' 
                  : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveFeatureTab('analytics')}
              >
                <FaChartBar className="mr-2" />
                Analytics
              </button>
              <button 
                className={`px-6 py-3 text-sm font-medium flex items-center ${activeFeatureTab === 'gamification' 
                  ? 'border-b-2 border-primary text-primary' 
                  : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveFeatureTab('gamification')}
              >
                <FaGamepad className="mr-2" />
                Gamification
              </button>
              <button 
                className={`px-6 py-3 text-sm font-medium flex items-center ${activeFeatureTab === 'communication' 
                  ? 'border-b-2 border-primary text-primary' 
                  : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveFeatureTab('communication')}
              >
                <FaComments className="mr-2" />
                Communication
              </button>
              <button 
                className={`px-6 py-3 text-sm font-medium flex items-center ${activeFeatureTab === 'mobile' 
                  ? 'border-b-2 border-primary text-primary' 
                  : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveFeatureTab('mobile')}
              >
                <FaMobileAlt className="mr-2" />
                Mobile
              </button>
              <button 
                className={`px-6 py-3 text-sm font-medium flex items-center ${activeFeatureTab === 'discover' 
                  ? 'border-b-2 border-primary text-primary' 
                  : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveFeatureTab('discover')}
              >
                <FaSearch className="mr-2" />
                Discover
              </button>
            </div>
            
            {/* Feature Component Display */}
            <div className="p-6">
              {renderFeatureComponent()}
            </div>
          </div>
        </div>
        
        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Enrolled courses */}
          <div className="lg:col-span-2">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow mb-6`}>
              <div className={`${darkMode ? 'border-gray-700' : 'border-b'} px-6 py-4`}>
                <h2 className="font-bold">Your Courses</h2>
              </div>
              <div className="p-6">
                {enrollments.length > 0 ? (
                  <div className="space-y-4">
                    {enrollments.slice(0, 3).map((enrollment) => {
                      const course = courses.find(c => c._id === enrollment.courseId);
                      const courseProgress = progress.find(p => p.courseId === enrollment.courseId);
                      const completionPercentage = courseProgress ? courseProgress.completionPercentage : 0;
                      
                      return (
                        <div key={enrollment._id} className={`${darkMode ? 'border-gray-700 bg-gray-700' : 'border'} container-rounded-lg p-4 hover:shadow-md transition-shadow`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className={`font-medium ${darkMode ? 'text-white' : ''}`}>{course?.title || 'Course'}</h3>
                              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{course?.instructor || 'Instructor'}</p>
                            </div>
                            <span className="text-sm font-medium text-primary">
                              {completionPercentage}% Complete
                            </span>
                          </div>
                          <div className={`mt-2 w-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} rounded-full h-2.5`}>
                            <div 
                              className="bg-primary h-2.5 rounded-full transition-all duration-300" 
                              style={{ width: `${completionPercentage}%` }}
                            ></div>
                          </div>
                          <div className="mt-3 flex gap-2">
                            <Link 
                              to={`/dashboard/courses/${course?._id}`}
                              className="text-sm text-primary hover:underline flex items-center"
                            >
                              <FaPlayCircle className="mr-1" />
                              Continue Learning
                            </Link>
                            <Link 
                              to={`/dashboard/courses/${course?._id}/notes`}
                              className={`text-sm ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-primary'} flex items-center`}
                            >
                              <FaDownload className="mr-1" />
                              Download Notes
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                    
                    {enrollments.length > 3 && (
                      <div className="text-center mt-4">
                        <Link 
                          to="/dashboard/courses" 
                          className="text-primary hover:underline"
                        >
                          View All Courses
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FaBook className={`text-4xl ${darkMode ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
                    <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No courses enrolled yet</p>
                    <Link 
                      to="/academy" 
                      className="text-primary hover:underline mt-2 inline-block"
                    >
                      Browse Courses
                    </Link>
                  </div>
                )}
              </div>
            </div>
            
            {/* Recent activity */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow`}>
              <div className={`${darkMode ? 'border-gray-700' : 'border-b'} px-6 py-4`}>
                <h2 className="font-bold">Recent Activity</h2>
              </div>
              <div className="p-6">
                {recentActivities.length > 0 ? (
                  <div className="space-y-4">
                    {recentActivities.map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-3">
                        <div className={`p-2 rounded-full ${
                          activity.type === 'completed' ? 'bg-green-100 text-green-600' : 
                          activity.type === 'started' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                        }`}>
                          {activity.type === 'completed' ? <FaCheckCircle /> : 
                           activity.type === 'started' ? <FaPlayCircle /> : <FaUserGraduate />}
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-medium text-sm ${darkMode ? 'text-white' : ''}`}>{activity.item}</h4>
                          <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{activity.course}</p>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>{formatDate(activity.date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No recent activity</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming deadlines */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow`}>
              <div className={`${darkMode ? 'border-gray-700' : 'border-b'} px-6 py-4`}>
                <h2 className="font-bold">Upcoming Deadlines</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {upcomingDeadlines.map((deadline) => (
                    <div key={deadline.id} className="flex items-start space-x-3">
                      <div className={`p-2 rounded-full ${
                        deadline.type === 'quiz' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'
                      }`}>
                        {deadline.type === 'quiz' ? <FaQuestionCircle /> : <FaFileAlt />}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-medium text-sm ${darkMode ? 'text-white' : ''}`}>{deadline.title}</h4>
                        <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{deadline.course}</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                          Due in {getDaysRemaining(deadline.dueDate)} days
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Quick links */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow`}>
              <div className={`${darkMode ? 'border-gray-700' : 'border-b'} px-6 py-4`}>
                <h2 className="font-bold">Quick Links</h2>
              </div>
              <div className="p-6">
                <ul className="space-y-2">
                  <li>
                    <Link 
                      to="/dashboard/courses" 
                      className="text-primary hover:underline flex items-center"
                    >
                      <FaBook className="mr-2" />
                      My Courses
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/dashboard/profile" 
                      className="text-primary hover:underline flex items-center"
                    >
                      <FaGraduationCap className="mr-2" />
                      My Profile
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/courses" 
                      className="text-primary hover:underline flex items-center"
                    >
                      <FaChartLine className="mr-2" />
                      Browse Courses
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const InstructorDashboard = () => (
    <>
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
              <FaBook className="text-xl" />
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Courses</p>
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>{stats.totalCourses}</h3>
            </div>
          </div>
        </div>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <FaUsers className="text-xl" />
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Students</p>
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>{stats.totalStudents}</h3>
            </div>
          </div>
        </div>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 text-yellow-600 mr-4">
              <FaChartBar className="text-xl" />
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Average Rating</p>
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>4.8</h3>
            </div>
          </div>
        </div>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4">
              <FaClock className="text-xl" />
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Pending Approval</p>
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>{stats.pendingApprovals}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My courses */}
        <div className="lg:col-span-2">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow mb-6`}>
            <div className={`${darkMode ? 'border-gray-700' : 'border-b'} px-6 py-4 flex justify-between items-center`}>
              <h2 className={`font-bold ${darkMode ? 'text-white' : ''}`}>My Courses</h2>
              <Link 
                to="/dashboard/courses/create" 
                className="bg-primary text-white px-4 py-2 container-rounded-lg hover:bg-primary-dark transition-colors flex items-center"
              >
                <FaPlus className="mr-2" />
                Create Course
              </Link>
            </div>
            <div className="p-6">
              {courses.filter(course => course.instructor === user._id).length > 0 ? (
                <div className="space-y-4">
                  {courses.filter(course => course.instructor === user._id).slice(0, 3).map((course) => (
                    <div key={course._id} className={`${darkMode ? 'border-gray-700 bg-gray-700' : 'border'} container-rounded-lg p-4 hover:shadow-md transition-shadow`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className={`font-medium ${darkMode ? 'text-white' : ''}`}>{course.title}</h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{course.category} • {course.level}</p>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>{course.studentsEnrolled} students enrolled</p>
                        </div>
                        <div className="flex gap-2">
                          <Link 
                            to={`/dashboard/courses/${course._id}/edit`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <FaEdit />
                          </Link>
                          <button className="text-red-600 hover:text-red-800">
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Link 
                          to={`/dashboard/courses/${course._id}`}
                          className="text-sm text-primary hover:underline flex items-center"
                        >
                          <FaEye className="mr-1" />
                          View Course
                        </Link>
                        <Link 
                          to={`/dashboard/courses/${course._id}/analytics`}
                          className={`text-sm ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-primary'} flex items-center`}
                        >
                          <FaChartBar className="mr-1" />
                          Analytics
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaBook className={`text-4xl ${darkMode ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No courses created yet</p>
                  <Link 
                    to="/dashboard/courses/create" 
                    className="text-primary hover:underline mt-2 inline-block"
                  >
                    Create Your First Course
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick actions */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow`}>
            <div className={`${darkMode ? 'border-gray-700' : 'border-b'} px-6 py-4`}>
              <h2 className={`font-bold ${darkMode ? 'text-white' : ''}`}>Quick Actions</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <Link 
                  to="/dashboard/courses/create"
                  className={`flex items-center p-3 container-rounded-lg ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border hover:bg-gray-50'} transition-colors`}
                >
                  <FaPlus className="mr-3 text-primary" />
                  <span className={darkMode ? 'text-white' : ''}>Create New Course</span>
                </Link>
                <Link 
                  to="/dashboard/assignments"
                  className={`flex items-center p-3 container-rounded-lg ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border hover:bg-gray-50'} transition-colors`}
                >
                  <FaFileAlt className="mr-3 text-primary" />
                  <span className={darkMode ? 'text-white' : ''}>Grade Assignments</span>
                </Link>
                <Link 
                  to="/dashboard/analytics"
                  className={`flex items-center p-3 container-rounded-lg ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border hover:bg-gray-50'} transition-colors`}
                >
                  <FaChartBar className="mr-3 text-primary" />
                  <span className={darkMode ? 'text-white' : ''}>View Analytics</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Recent students */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow`}>
            <div className={`${darkMode ? 'border-gray-700' : 'border-b'} px-6 py-4`}>
              <h2 className={`font-bold ${darkMode ? 'text-white' : ''}`}>Recent Students</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {enrollments.slice(0, 3).map((enrollment) => (
                  <div key={enrollment._id} className="flex items-center space-x-3">
                    <div className={`w-8 h-8 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full flex items-center justify-center`}>
                      <FaUserGraduate className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-medium text-sm ${darkMode ? 'text-white' : ''}`}>Student Name</h4>
                      <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Enrolled in Course</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const AdminDashboard = () => (
    <>
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
              <FaBook className="text-xl" />
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Courses</p>
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>{stats.totalCourses}</h3>
            </div>
          </div>
        </div>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <FaUsers className="text-xl" />
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Students</p>
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>{stats.totalStudents}</h3>
            </div>
          </div>
        </div>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
              <FaUserTie className="text-xl" />
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Instructors</p>
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>24</h3>
            </div>
          </div>
        </div>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4">
              <FaClock className="text-xl" />
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Pending Approval</p>
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>{stats.pendingApprovals}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending approvals */}
        <div className="lg:col-span-2">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow mb-6`}>
            <div className={`${darkMode ? 'border-gray-700' : 'border-b'} px-6 py-4`}>
              <h2 className={`font-bold ${darkMode ? 'text-white' : ''}`}>Pending Course Approvals</h2>
            </div>
            <div className="p-6">
              {courses.filter(course => !course.isPublished).length > 0 ? (
                <div className="space-y-4">
                  {courses.filter(course => !course.isPublished).slice(0, 3).map((course) => (
                    <div key={course._id} className={`${darkMode ? 'border-gray-700 bg-gray-700' : 'border'} container-rounded-lg p-4 hover:shadow-md transition-shadow`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className={`font-medium ${darkMode ? 'text-white' : ''}`}>{course.title}</h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>By {course.instructor}</p>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>{course.category} • {course.level}</p>
                        </div>
                        <div className="flex gap-2">
                          <button className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600">
                            Approve
                          </button>
                          <button className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600">
                            Reject
                          </button>
                        </div>
                      </div>
                      <div className="mt-3">
                        <Link 
                          to={`/dashboard/courses/${course._id}/review`}
                          className="text-sm text-primary hover:underline flex items-center"
                        >
                          <FaEye className="mr-1" />
                          Review Course
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaCheckCircle className={`text-4xl ${darkMode ? 'text-green-600' : 'text-green-300'} mx-auto mb-4`} />
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No pending approvals</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Student Management Section */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow-md p-6 mb-6`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Student Management</h3>
              <Link to="/dashboard/students" className="text-primary hover:underline flex items-center">
                <span>View All Students</span>
                <FaArrowRight className="ml-1" />
              </Link>
            </div>
            
            {/* Recent Enrollments */}
            <div className="mb-6">
              <h4 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-700'} mb-3`}>Recent Enrollments</h4>
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Student</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Course</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Date</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {mockEnrollments.slice(0, 3).map((enrollment, index) => {
                      const course = mockCourses.find(c => c.id === enrollment.courseId);
                      return (
                        <tr key={index}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`h-8 w-8 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center mr-3`}>
                                <FaUserGraduate className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                              </div>
                              <div>
                                <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{enrollment.studentName}</div>
                                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{enrollment.studentEmail}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{course ? course.title : 'Unknown Course'}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{formatDate(enrollment.enrollmentDate)}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <Link to={`/dashboard/students/${enrollment.studentId}`} className="text-primary hover:text-primary-dark mr-3">
                              <FaEye />
                            </Link>
                            <button className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
                              <FaEdit />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-3">
                <Link to="/dashboard/enrollments" className="text-sm text-primary hover:underline">View all enrollments</Link>
              </div>
            </div>
            
            {/* Student Progress Tracking */}
            <div className="mb-6">
              <h4 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-700'} mb-3`}>Student Progress Tracking</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mockProgress.slice(0, 4).map((item, index) => {
                  const course = mockCourses.find(c => c.id === item.courseId);
                  return (
                    <div key={index} className={`${darkMode ? 'border-gray-700 bg-gray-700' : 'border'} container-rounded-lg p-4`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className={`font-medium ${darkMode ? 'text-white' : ''}`}>{item.studentName}</h5>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{course ? course.title : 'Unknown Course'}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs ${item.progress >= 75 ? 'bg-green-100 text-green-800' : item.progress >= 50 ? 'bg-blue-100 text-blue-800' : item.progress >= 25 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                          {item.progress}% Complete
                        </span>
                      </div>
                      <div className={`w-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} rounded-full h-2.5`}>
                        <div 
                          className={`h-2.5 rounded-full ${item.progress >= 75 ? 'bg-green-500' : item.progress >= 50 ? 'bg-blue-500' : item.progress >= 25 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${item.progress}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between mt-3">
                        <Link to={`/dashboard/students/${item.studentId}`} className="text-xs text-primary hover:underline flex items-center">
                          <FaChartLine className="mr-1" />
                          <span>View Details</span>
                        </Link>
                        <button className={`text-xs ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:underline'} flex items-center`}>
                          <FaEdit className="mr-1" />
                          <span>Edit</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3">
                <Link to="/dashboard/progress" className="text-sm text-primary hover:underline">View all progress</Link>
              </div>
            </div>
            
            {/* Student Support */}
            <div>
              <h4 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-700'} mb-3`}>Student Support</h4>
              <div className="flex flex-wrap gap-4">
                <Link to="/dashboard/support/create" className={`flex-1 min-w-[200px] ${darkMode ? 'border-gray-700 bg-gray-700 hover:bg-gray-600' : 'border hover:bg-gray-50'} container-rounded-lg p-4 transition-colors`}>
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-primary-light flex items-center justify-center mr-3">
                      <FaTicketAlt className="text-primary" />
                    </div>
                    <div>
                      <h5 className={`font-medium ${darkMode ? 'text-white' : ''}`}>Create Support Ticket</h5>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Assist students with issues</p>
                    </div>
                  </div>
                </Link>
                <Link to="/dashboard/support/reports" className={`flex-1 min-w-[200px] ${darkMode ? 'border-gray-700 bg-gray-700 hover:bg-gray-600' : 'border hover:bg-gray-50'} container-rounded-lg p-4 transition-colors`}>
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-primary-light flex items-center justify-center mr-3">
                      <FaChartBar className="text-primary" />
                    </div>
                    <div>
                      <h5 className={`font-medium ${darkMode ? 'text-white' : ''}`}>Support Reports</h5>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>View support analytics</p>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick actions */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow`}>
            <div className={`${darkMode ? 'border-gray-700' : 'border-b'} px-6 py-4`}>
              <h2 className={`font-bold ${darkMode ? 'text-white' : ''}`}>Quick Actions</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <Link 
                  to="/dashboard/students"
                  className={`flex items-center p-3 container-rounded-lg ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border hover:bg-gray-50'} transition-colors`}
                >
                  <FaUserGraduate className="mr-3 text-primary" />
                  <span className={darkMode ? 'text-white' : ''}>Student Management</span>
                </Link>
                <Link 
                  to="/dashboard/users"
                  className={`flex items-center p-3 container-rounded-lg ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border hover:bg-gray-50'} transition-colors`}
                >
                  <FaUsers className="mr-3 text-primary" />
                  <span className={darkMode ? 'text-white' : ''}>Manage Users</span>
                </Link>
                <Link 
                  to="/dashboard/analytics"
                  className={`flex items-center p-3 container-rounded-lg ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border hover:bg-gray-50'} transition-colors`}
                >
                  <FaChartBar className="mr-3 text-primary" />
                  <span className={darkMode ? 'text-white' : ''}>Site Analytics</span>
                </Link>
                <Link 
                  to="/dashboard/settings"
                  className={`flex items-center p-3 container-rounded-lg ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border hover:bg-gray-50'} transition-colors`}
                >
                  <FaCog className="mr-3 text-primary" />
                  <span className={darkMode ? 'text-white' : ''}>System Settings</span>
                </Link>
              </div>
            </div>
          </div>

          {/* System stats */}
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} container-rounded-lg shadow`}>
            <div className={`${darkMode ? 'border-gray-700' : 'border-b'} px-6 py-4`}>
              <h2 className={`font-bold ${darkMode ? 'text-white' : ''}`}>System Stats</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Active Users</span>
                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : ''}`}>1,234</span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total Revenue</span>
                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : ''}`}>$45,678</span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Courses Created</span>
                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : ''}`}>89</span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Completion Rate</span>
                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : ''}`}>78%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Loading state
  if (enrollmentsLoading || coursesLoading || progressLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div className={`p-6 ${darkMode ? 'bg-gray-900 text-white' : 'bg-white'}`}>
        <div className="flex justify-between items-center mb-6">
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : ''}`}>
            Welcome back, {user?.name}!
            {user?.role === 'admin' && <FaCrown className="inline ml-2 text-yellow-500" />}
            {user?.role === 'instructor' && <FaUserTie className="inline ml-2 text-blue-500" />}
            {user?.role === 'student' && <FaUserGraduate className="inline ml-2 text-green-500" />}
          </h1>
          <button
            onClick={() => dispatch(toggleDarkMode())}
            className={`p-2 container-rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
        
        {getRoleBasedDashboard()}
      </div>
    </>
  );
};

export default Dashboard;